import { Dropdown, Thumbnail, Toolbar } from '@bynder/design-system';

import type { Editor } from '@tiptap/react';
import {
  type ColorOption,
  DEFAULT_COLOR_OPTIONS,
} from '../../data/configs/colors.ts';
import { getValueOfCssVariable } from '../../helpers/dom.ts';

type ColorPickerDropdownProps = {
  optionsOnly?: boolean;
  onChange: (color: ColorOption) => void;
  editor: Editor;
  colorOptions?: ColorOption[];
};

export const ColorPickerDropdown = ({
  editor,
  optionsOnly = false,
  onChange,
  colorOptions,
}: ColorPickerDropdownProps) => {
  const colors =
    colorOptions && Array.isArray(colorOptions) && colorOptions.length !== 0
      ? colorOptions
      : DEFAULT_COLOR_OPTIONS;

  const defaultColorOption = { label: 'Default', value: '' };
  const bynderDefaultFontColour = '#334E5C';
  const combinedColors = [defaultColorOption, ...colors];

  function renderOptions() {
    return (
      <>
        {combinedColors.map(({ label, value }) => {
          const isVarColour = value.startsWith('var(');
          const isDefaultColour = value === '';

          if (isVarColour) {
            const computedValue = getValueOfCssVariable(value);
            value = computedValue ? computedValue : value;
          }
          return (
            <Dropdown.ListItem
              onClick={() => {
                // here we want the value of the default to be the default font color so that
                // if selected font colour will return to what users first see
                const valueToPass = isDefaultColour
                  ? bynderDefaultFontColour
                  : value;

                onChange({ label, value: valueToPass });
              }}
              key={label}
              isChecked={editor.isActive('textStyle', {
                color: value,
              })}
              thumbnail={
                <Thumbnail
                  variant="content"
                  // here we do not want the value to be populated if default as
                  // that will try to fill the thumbnail preview which we want to remain
                  // as it is which is only possible by leaving the value empty
                  backgroundColor={isDefaultColour ? '' : value}
                  shape="circle"
                  size="s"
                />
              }
              subtext={value}
              // biome-ignore lint/a11y/useSemanticElements: Biome wants us to use a select/option here, but the select in the DS is not suitable for our use case as it has no onClick
              role="option"
              aria-label={`Select ${label}`}
              aria-selected={editor.isActive('textStyle', {
                color: value,
              })}
            >
              {label}
            </Dropdown.ListItem>
          );
        })}
      </>
    );
  }

  if (optionsOnly) {
    return renderOptions();
  }

  const currentTextColour = editor.getAttributes('textStyle').color;

  // editor.getAttributes only seems to work when styles are applied via TipTap
  // this function is to pull out colour - or any other styles if necessary later - that are already applied via things like classes in the themeset
  const getCurrentComputedTextColor = (editor: Editor) => {
    if (!editor.isFocused) {
      return;
    }

    const { from } = editor.state.selection;

    // this will grab the node where our cursor currently is
    const domNode = editor.view.domAtPos(from)?.node;

    const domElement =
      domNode instanceof HTMLElement ? domNode : domNode?.parentElement;

    if (domElement) {
      const computedStyle = window.getComputedStyle(domElement);
      return computedStyle.color;
    }

    return '';
  };

  return (
    <Dropdown
      minWidth={'224px!important'}
      position="bottom"
      // biome-ignore lint/a11y/useSemanticElements: Biome wants us to use a select/option here, but the select in the DS is not suitable for our use case as it has no onClick
      role="listbox"
      trigger={({ isOpen, ...triggerProps }) => (
        <Toolbar.Button
          isPressed={isOpen}
          rightIcon={<Dropdown.Arrow />}
          isFullWidth
          {...triggerProps}
          aria-haspopup="menu"
          aria-label="Select font colour"
        >
          <Dropdown.SelectButtonText>
            <Thumbnail
              variant="content"
              backgroundColor={
                currentTextColour
                  ? currentTextColour
                  : getCurrentComputedTextColor(editor)
              }
              shape="circle"
              size="s"
            />
          </Dropdown.SelectButtonText>
        </Toolbar.Button>
      )}
    >
      {renderOptions()}
    </Dropdown>
  );
};
